import React, {
  Fragment,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useTheme } from '../../ThemeContext';

import Tabs from '../ApiDescriptionTabs';

import {
  Wrapper,
  Information,
  Item,
  Column,
  Title,
  Param,
  Details,
  Attributes,
  AttributesTitle,
  AttributesContent,
  Body,
  BodyTitle,
  BodyContent,
  Response,
  ResponseTitle,
  ResponseContent,
} from './ApiDescription.style';

const ApiDescription = forwardRef(({ data = {} }, ref) => {
  const apiDescriptionRef = useRef([]);
  const intl = useIntl();
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);

  const handleScrollEventListener = (event) => {
    if (apiDescriptionRef.current && apiDescriptionRef.current.length > 0) {
      let newCurrentTab = 0;
      const containerBounds = event.target.getBoundingClientRect();
      const containerStartBound = containerBounds.top;
      const containerEndBound = containerStartBound + containerBounds.height;
      apiDescriptionRef.current.forEach((item, key, currentArray) => {
        if (item) {
          const itemBounds = item.getBoundingClientRect();
          const itemStartBound = itemBounds.top;
          const itemEndBound = itemStartBound + itemBounds.height;

          if (
            itemStartBound <= containerStartBound &&
            itemEndBound <= containerEndBound
          ) {
            const nextSibling = currentArray[key + 1];
            if (nextSibling) newCurrentTab = key + 1;
            else newCurrentTab = key;
          } else if (
            itemStartBound >= containerStartBound &&
            itemEndBound <= containerEndBound
          )
            newCurrentTab = key;
          else if (
            itemStartBound <= containerStartBound &&
            itemEndBound >= containerEndBound
          )
            newCurrentTab = key;
        }
      });
      setCurrentTab(newCurrentTab);
    }
  };

  const handleManualSelectionItem = (key) => {
    if (apiDescriptionRef.current && apiDescriptionRef.current.length > 0) {
      apiDescriptionRef.current[key].scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  };

  useImperativeHandle(ref, (event) => ({
    handleScrollEventListener(event) {
      handleScrollEventListener(event);
    },
  }));

  return (
    <Wrapper>
      {data.endpoints && data.endpoints.length > 0 ? (
        <Tabs
          currentTab={currentTab}
          tabs={data.endpoints.map((endpoint, key) => ({
            key: key,
            name: endpoint.title,
          }))}
          handleChangeTab={(key) => {
            handleManualSelectionItem(key);
          }}
        />
      ) : (
        ''
      )}
      {data.description ? (
        <Information>
          <pre>{data.description}</pre>
        </Information>
      ) : (
        ''
      )}
      {data.endpoints && data.endpoints.length > 0 ? (
        data.endpoints.map((endpoint, key) => (
          <Item
            key={key}
            theme={theme.apiDescription.item}
            ref={(node) => (apiDescriptionRef.current[key] = node)}
          >
            <Column>
              <Title theme={theme.apiDescription.title}>{endpoint.title}</Title>
              {endpoint.url && (
                <Param theme={theme.apiDescription.param}>
                  URL: {endpoint.url}
                </Param>
              )}
              {endpoint.method && (
                <Param theme={theme.apiDescription.param}>
                  METHOD: {endpoint.method}
                </Param>
              )}
              <Details theme={theme.apiDescription.details}>
                <pre>{endpoint.description}</pre>
              </Details>
              {endpoint.attributes && endpoint.attributes.length > 0 && (
                <Attributes>
                  <AttributesTitle>ATTRIBUTES</AttributesTitle>
                  <AttributesContent
                    theme={theme.apiDescription.attributesContent}
                  >
                    {endpoint.attributes.map((attribute, key) => (
                      <Fragment key={key}>
                        {attribute.name}
                        {attribute.type || attribute.description
                          ? ':'
                          : ''}{' '}
                        {attribute.type ? `${'<'}${attribute.type}${'>'}` : ''}{' '}
                        {attribute.type && attribute.description ? '-' : ''}{' '}
                        {attribute.description ? attribute.description : ''}
                        <br />
                      </Fragment>
                    ))}
                  </AttributesContent>
                </Attributes>
              )}
              {endpoint.body && (
                <Body>
                  <BodyTitle>BODY</BodyTitle>
                  <BodyContent theme={theme.apiDescription.bodyContent}>
                    <pre>
                      {JSON.stringify(JSON.parse(endpoint.body), null, '\t')}
                    </pre>
                  </BodyContent>
                </Body>
              )}
            </Column>
            <Column>
              {endpoint.responses && endpoint.responses.length > 0 && (
                <Response>
                  <ResponseTitle theme={theme.apiDescription.responseTitle}>
                    RESPONSE
                  </ResponseTitle>
                  <ResponseContent theme={theme.apiDescription.responseContent}>
                    {endpoint.responses.map((response, key) => (
                      <Fragment key={key}>
                        Code: {response.code}
                        {response.response ? <br /> : ''}
                        <pre>
                          {response.response
                            ? JSON.stringify(
                                JSON.parse(response.response),
                                null,
                                '\t'
                              )
                            : ''}
                        </pre>
                        <br />
                        <br />
                      </Fragment>
                    ))}
                  </ResponseContent>
                </Response>
              )}
            </Column>
          </Item>
        ))
      ) : (
        <Information>
          {intl.formatMessage({
            id: `API_DESCRIPTION.NO_ENDPOINTS`,
          })}
        </Information>
      )}
    </Wrapper>
  );
});

ApiDescription.propTypes = {
  data: PropTypes.object,
};

export default ApiDescription;
